import { useState, useEffect, useCallback, useRef } from 'react'
import { emailTemplateApi, EmailTemplateListItem, EmailTemplateDetail } from '@/services/api/superadmin'
import { extractErrorMessage } from '@/utils/errorHandler'
import { EmailBrandingEditor } from '@/components/email-templates/EmailBrandingEditor'
import type { BrandingConfig } from '@/components/email-templates/EmailBrandingEditor'
import { EmailHtmlPreview } from '@/components/email-templates/EmailHtmlPreview'
import { EmailSettingsSection } from '@/components/config/EmailSettingsSection'
import { configApi } from '@/services/api/config'
import {
  Save,
  X,
  GitFork,
  RotateCcw,
  Eye,
  AlertTriangle,
  CheckCircle,
  FileText,
  Code,
  Palette,
  Mail,
} from 'lucide-react'

const DEFAULT_SIMPLE_VARS: Record<string, string> = {
  recipient_name: 'Max Mustermann',
  requesting_teacher: 'Hans Müller',
  substitute_name: 'Anna Schmidt',
  teacher_name: 'Lisa Weber',
  date: '15.05.2026',
  time: '08:15 - 09:00',
  time_slot_name: 'Lektion 2',
  start_time: '08:15',
  end_time: '09:00',
  subject: 'Mathematik',
  subject_name: 'Mathematik',
  klass: '3a',
  klass_name: '3a',
  room: 'R101',
  room_name: 'R101',
  accept_url: 'https://example.com/accept?token=abc123',
  decline_url: 'https://example.com/decline?token=abc123',
  cancel_url: 'https://example.com/cancel?token=abc123',
  admin_url: 'https://example.com/superadmin/dashboard',
  minutes_until_start: '45',
  hours_until: '2',
  count: '3',
  dashboard_url: 'https://example.com/dashboard',
}

const COMPLEX_VARS: Record<string, unknown> = {
  requests: [
    { date: '15.05.2026', time: '08:15', subject: 'Mathe', klass: '3a', room: 'R101', requesting_teacher: 'Hans Müller', accept_url: '#', decline_url: '#' },
    { date: '15.05.2026', time: '09:15', subject: 'Physik', klass: '2b', room: 'R205', requesting_teacher: 'Peter Schmidt', accept_url: '#', decline_url: '#' },
  ],
}

type EditorTab = 'content' | 'css' | 'branding'

type DraftDataFields = {
  name: string
  description: string | null
  subject_template: string
  body_html_template: string
  body_text_template: string | null
  action_buttons: Record<string, unknown>[] | null
  css_styles: string | null
  branding_config: Record<string, unknown> | null
  branding_source: 'school_design' | 'custom'
  is_active: boolean
}

export function EmailTemplatePreviewPage() {
  const [templates, setTemplates] = useState<EmailTemplateListItem[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<EmailTemplateDetail | null>(null)
  const [draftData, setDraftData] = useState<Partial<DraftDataFields>>({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [preview, setPreview] = useState<{
    subject: string
    body_html: string
    body_text: string | null
    variables_used: string[]
    variables_missing: string[]
    parse_errors: string[]
  } | null>(null)
  const [sampleVars, setSampleVars] = useState<Record<string, unknown>>({})
  const [activeTab, setActiveTab] = useState<EditorTab>('content')
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [schoolDesign, setSchoolDesign] = useState<Record<string, unknown> | null>(null)
  const [view, setView] = useState<'settings' | 'templates'>('settings')
  const [previewLoading, setPreviewLoading] = useState(false)
  const previewSeq = useRef(0)

  // Schul-Design-Stamm laden: dient als Prefill, wenn ein Template auf
  // «Eigenes Design» umgestellt wird (Startpunkt = aktuelles Schul-Design).
  useEffect(() => {
    configApi.getConfig('design')
      .then(res => setSchoolDesign(res.config ?? null))
      .catch(() => { /* design config optional */ })
  }, [])

  useEffect(() => {
    loadTemplates()
  }, [])

  useEffect(() => {
    if (!hasUnsavedChanges) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsavedChanges])

  async function loadTemplates() {
    setLoading(true)
    try {
      const data = await emailTemplateApi.listEmailTemplates()
      setTemplates(data)
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Templates konnten nicht geladen werden' }))
    } finally {
      setLoading(false)
    }
  }

  const selectTemplate = useCallback(async (id: string) => {
    if (hasUnsavedChanges) {
      if (!confirm('Ungespeicherte Änderungen verwerfen?')) return
    }
    setLoading(true)
    setError(null)
    setSuccess(null)
    setPreview(null)
    try {
      const data = await emailTemplateApi.getEmailTemplate(id)
      setDetail(data)
      setDraftData({
        name: data.name,
        description: data.description,
        subject_template: data.subject_template,
        body_html_template: data.body_html_template,
        body_text_template: data.body_text_template,
        action_buttons: data.action_buttons,
        css_styles: data.css_styles,
        branding_config: data.branding_config,
        branding_source: data.branding_source ?? 'school_design',
        is_active: data.is_active,
      })
      setHasUnsavedChanges(false)
      const vars = initSampleVars(data.template_key, data.required_variables || [])
      setSelectedId(id)
      await loadPreview(id, vars, {
        subject_template: data.subject_template,
        body_html_template: data.body_html_template,
        body_text_template: data.body_text_template,
        css_styles: data.css_styles,
        branding_config: data.branding_config,
        branding_source: data.branding_source ?? 'school_design',
      })
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Template konnte nicht geladen werden' }))
    } finally {
      setLoading(false)
    }
  }, [hasUnsavedChanges])

  function initSampleVars(_templateKey: string, requiredVars: string[]) {
    const vars: Record<string, unknown> = {}
    for (const v of requiredVars) {
      if (COMPLEX_VARS[v]) {
        vars[v] = COMPLEX_VARS[v]
      } else if (DEFAULT_SIMPLE_VARS[v]) {
        vars[v] = DEFAULT_SIMPLE_VARS[v]
      } else if (v.endsWith('_url')) {
        vars[v] = 'https://example.com/...'
      } else if (v.includes('count') || v.includes('minutes') || v.includes('hours')) {
        vars[v] = '1'
      } else {
        vars[v] = ''
      }
    }
    setSampleVars(vars)
    return vars
  }

  async function loadPreview(
    id: string,
    vars: Record<string, unknown>,
    draft?: Partial<DraftDataFields>,
  ) {
    const seq = ++previewSeq.current
    setPreviewLoading(true)
    try {
      const result = await emailTemplateApi.renderEmailPreview(id, vars, draft)
      if (seq !== previewSeq.current) return
      setPreview(result)
    } catch (err: unknown) {
      if (seq !== previewSeq.current) return
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Rendern' }))
    } finally {
      if (seq === previewSeq.current) setPreviewLoading(false)
    }
  }

  async function handleSave() {
    if (!selectedId || !detail) return
    setSaving(true)
    setError(null)
    try {
      const updatePayload: Record<string, unknown> = {}
      const fields: (keyof DraftDataFields)[] = ['name', 'description', 'subject_template', 'body_html_template', 'body_text_template', 'action_buttons', 'css_styles', 'branding_config', 'branding_source', 'is_active']
      for (const key of fields) {
        if (draftData[key] !== undefined) {
          updatePayload[key] = draftData[key]
        }
      }
      const updated = await emailTemplateApi.updateEmailTemplate(selectedId, updatePayload)
      setDetail(updated)
      setDraftData({
        name: updated.name,
        description: updated.description,
        subject_template: updated.subject_template,
        body_html_template: updated.body_html_template,
        body_text_template: updated.body_text_template,
        action_buttons: updated.action_buttons,
        css_styles: updated.css_styles,
        branding_config: updated.branding_config,
        branding_source: updated.branding_source ?? 'school_design',
        is_active: updated.is_active,
      })
      setHasUnsavedChanges(false)
      setSuccess('Template gespeichert')
      loadTemplates()
      void loadPreview(selectedId, sampleVars, {
        subject_template: updated.subject_template,
        body_html_template: updated.body_html_template,
        body_text_template: updated.body_text_template,
        css_styles: updated.css_styles,
        branding_config: updated.branding_config,
        branding_source: updated.branding_source ?? 'school_design',
      })
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Speichern' }))
    } finally {
      setSaving(false)
    }
  }

  async function handleFork() {
    if (!selectedId) return
    try {
      const forked = await emailTemplateApi.forkEmailTemplate(selectedId)
      setSuccess('Template-Kopie erstellt')
      await loadTemplates()
      await selectTemplate(forked.id)
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Forken' }))
    }
  }

  async function handleReset() {
    if (!selectedId || !detail || detail.is_system) return
    if (!confirm('Template wirklich zurücksetzen? Alle Änderungen gehen verloren.')) return
    try {
      await emailTemplateApi.resetEmailTemplate(selectedId)
      setSuccess('Template zurückgesetzt')
      setSelectedId(null)
      setDetail(null)
      setDraftData({})
      loadTemplates()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Zurücksetzen' }))
    }
  }

  async function handleResetAll() {
    if (!confirm('Wirklich ALLE bearbeiteten Templates zurücksetzen?')) return
    if (!confirm('Dies kann nicht rückgängig gemacht werden. Fortfahren?')) return
    try {
      const res = await emailTemplateApi.resetAllEmailTemplates()
      setSuccess(`${res.reset_count} Template(s) zurückgesetzt`)
      setSelectedId(null)
      setDetail(null)
      setDraftData({})
      loadTemplates()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Zurücksetzen' }))
    }
  }

  async function handlePreview() {
    if (!selectedId) return
    await loadPreview(selectedId, sampleVars, {
      subject_template: draftData.subject_template as string | undefined,
      body_html_template: draftData.body_html_template as string | undefined,
      body_text_template: draftData.body_text_template as string | null | undefined,
      css_styles: draftData.css_styles as string | null | undefined,
      branding_config: draftData.branding_config as Record<string, unknown> | null | undefined,
      branding_source: (draftData.branding_source as 'school_design' | 'custom' | undefined) ?? 'school_design',
    })
  }

  function updateDraft(field: string, value: unknown) {
    setDraftData(d => ({ ...d, [field]: value }))
    setHasUnsavedChanges(true)
  }

  function discardChanges() {
    if (!detail) return
    setDraftData({
      name: detail.name,
      description: detail.description,
      subject_template: detail.subject_template,
      body_html_template: detail.body_html_template,
      body_text_template: detail.body_text_template,
      action_buttons: detail.action_buttons,
      css_styles: detail.css_styles,
      branding_config: detail.branding_config,
      branding_source: detail.branding_source ?? 'school_design',
      is_active: detail.is_active,
    })
    setHasUnsavedChanges(false)
  }

  if (view === 'templates' && loading && !detail) {
    return <div className="p-6 text-gray-500">Laden...</div>
  }

  return (
    <div className="flex h-full">
      {/* Sidebar — eine flache Liste: E-Mail-Versand + Templates */}
      <div className="w-72 bg-gray-50 border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">E-Mail</h2>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          <button
            type="button"
            onClick={() => setView('settings')}
            className={`w-full text-left px-4 py-2.5 flex items-start gap-2.5 transition ${view === 'settings' ? 'bg-indigo-50 border-l-2 border-indigo-600' : 'border-l-2 border-transparent hover:bg-gray-100'}`}
          >
            <Mail className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">E-Mail-Versand</span>
              <span className="block text-xs text-gray-500 leading-snug mt-0.5">SMTP-Server, Absender und Versand-Modus.</span>
            </span>
          </button>

          {templates.length === 0 && !loading && (
            <div className="px-4 py-8 text-center text-sm text-gray-400">Keine Templates vorhanden</div>
          )}
          {templates.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setView('templates')
                void selectTemplate(item.id)
              }}
              className={`w-full text-left px-4 py-2.5 flex items-start gap-2.5 transition ${selectedId === item.id && view === 'templates' ? 'bg-indigo-50 border-l-2 border-indigo-600' : 'border-l-2 border-transparent hover:bg-gray-100'}`}
            >
              {item.has_override ? (
                <CheckCircle className="w-3.5 h-3.5 text-yellow-500 mt-0.5 shrink-0" />
              ) : (
                <span className="w-3.5 h-3.5 rounded-full bg-gray-300 mt-0.5 shrink-0" />
              )}
              <span className="min-w-0">
                <span className="block text-sm text-gray-800 truncate">{item.name}</span>
                {item.description && (
                  <span className="block text-xs text-gray-500 leading-snug mt-0.5 line-clamp-2">{item.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>

        <div className="p-3 border-t border-gray-200">
          <button
            onClick={handleResetAll}
            className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" /> Alle Templates zurücksetzen
          </button>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {view === 'settings' ? (
          <EmailSettingsSection />
        ) : (
          <>
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 text-red-700 rounded flex items-center justify-between">
            <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        )}
        {success && (
          <div className="mx-4 mt-4 p-3 bg-green-50 text-green-700 rounded flex items-center justify-between">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4" /> {success}</span>
            <button onClick={() => setSuccess(null)}><X className="w-4 h-4" /></button>
          </div>
        )}

        {!detail ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            Wähle ein Template aus der Sidebar
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-white">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-gray-900">{detail.name}</h3>
                <span className="text-xs text-gray-400">v{detail.version}</span>
                {detail.is_system && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">System</span>}
                {!detail.is_system && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Bearbeitet</span>}
              </div>
              <div className="flex items-center gap-2">
                {detail.is_system && (
                  <button onClick={handleFork} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                    <GitFork className="w-3 h-3" /> Forken
                  </button>
                )}
                {!detail.is_system && (
                  <button onClick={handleReset} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1">
                    <RotateCcw className="w-3 h-3" /> Zurücksetzen
                  </button>
                )}
                {hasUnsavedChanges && (
                  <>
                    <button onClick={discardChanges} className="text-sm text-gray-600 hover:text-gray-700">
                      Verwerfen
                    </button>
                    <button onClick={handleSave} disabled={saving} className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                      <Save className="w-3 h-3" /> {saving ? '...' : 'Speichern'}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Content + Preview split */}
            <div className="flex-1 flex overflow-hidden">
              {/* Editor */}
              <div className="flex-1 flex flex-col border-r border-gray-200 overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-gray-200 bg-gray-50">
                  {([['content', 'Inhalt', FileText], ['css', 'CSS', Code], ['branding', 'Branding', Palette]] as [EditorTab, string, React.ElementType][]).map(([key, label, Icon]) => (
                    <button
                      key={key}
                      onClick={() => setActiveTab(key)}
                      className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 ${activeTab === key ? 'border-blue-600 text-blue-700 bg-white' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                      <Icon className="w-3 h-3" /> {label}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {activeTab === 'content' && (
                    <div className="space-y-4 max-w-3xl">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                        <input
                          type="text"
                          value={(draftData.name as string) ?? ''}
                          onChange={e => updateDraft('name', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Beschreibung</label>
                        <textarea
                          value={(draftData.description as string) ?? ''}
                          onChange={e => updateDraft('description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Betreff</label>
                        <input
                          type="text"
                          value={(draftData.subject_template as string) ?? ''}
                          onChange={e => updateDraft('subject_template', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Body HTML</label>
                        <textarea
                          value={(draftData.body_html_template as string) ?? ''}
                          onChange={e => updateDraft('body_html_template', e.target.value)}
                          rows={15}
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Body Text</label>
                        <textarea
                          value={(draftData.body_text_template as string) ?? ''}
                          onChange={e => updateDraft('body_text_template', e.target.value)}
                          rows={8}
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                      {detail.required_variables && detail.required_variables.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Benötigte Variablen</label>
                          <div className="flex flex-wrap gap-1">
                            {detail.required_variables.map(v => (
                              <span key={v} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{v}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'css' && (
                    <div className="max-w-3xl">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Custom CSS</label>
                      <textarea
                        value={(draftData.css_styles as string) ?? ''}
                        onChange={e => updateDraft('css_styles', e.target.value)}
                        rows={20}
                        style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        placeholder="/* Custom CSS für dieses Template */"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                  )}

                  {activeTab === 'branding' && (
                    <div className="max-w-3xl">
                      <EmailBrandingEditor
                        brandingConfig={draftData.branding_config as BrandingConfig | null}
                        brandingSource={(draftData.branding_source as 'school_design' | 'custom') ?? 'school_design'}
                        schoolDesign={schoolDesign}
                        onChange={(config) => updateDraft('branding_config', config)}
                        onSourceChange={(source) => updateDraft('branding_source', source)}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Preview panel */}
              <div className="w-1/2 flex flex-col overflow-hidden bg-gray-50">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Eye className="w-4 h-4" /> Vorschau
                  </h4>
                  <button onClick={() => void handlePreview()} disabled={previewLoading} className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50">
                    {previewLoading ? 'Wird gerendert…' : 'Aktualisieren'}
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Sample vars form */}
                  <div className="bg-white rounded-lg border p-3">
                    <h5 className="text-xs font-semibold text-gray-500 mb-2">Sample Variablen</h5>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(sampleVars).map(([key, value]) => (
                        <div key={key}>
                          <label className="block text-[10px] font-medium text-gray-400">{key}</label>
                          {typeof value === 'object' && value !== null ? (
                            <span className="text-[10px] text-gray-400">[{Array.isArray(value) ? `${value.length} items` : 'object'}]</span>
                          ) : (
                            <input
                              type="text"
                              value={String(value ?? '')}
                              onChange={e => setSampleVars(v => ({ ...v, [key]: e.target.value }))}
                              className="w-full px-2 py-1 border rounded text-[11px]"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {previewLoading && !preview && (
                    <div className="text-xs text-gray-500 px-1">Vorschau wird gerendert…</div>
                  )}

                  {preview && (
                    <div className="bg-white rounded-lg border overflow-hidden">
                      <div className="px-4 py-2 bg-gray-100 border-b">
                        <span className="text-xs font-medium text-gray-600">Betreff: </span>
                        <span className="text-xs text-gray-700">{preview.subject}</span>
                      </div>
                      <EmailHtmlPreview html={preview.body_html} />
                      {preview.body_text && (
                        <div className="px-4 pb-4 border-t">
                          <details>
                            <summary className="text-xs cursor-pointer text-gray-500">Plain Text ansehen</summary>
                            <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">{preview.body_text}</pre>
                          </details>
                        </div>
                      )}
                    </div>
                  )}

                  {preview && preview.variables_missing.length > 0 && (
                    <div className="flex items-center gap-2 text-xs text-yellow-700 bg-yellow-50 rounded p-2">
                      <AlertTriangle className="w-3 h-3" />
                      Fehlende Variablen: {preview.variables_missing.join(', ')}
                    </div>
                  )}

                  {preview && preview.parse_errors.length > 0 && (
                    <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded p-2">
                      <AlertTriangle className="w-3 h-3" />
                      Parse Errors: {preview.parse_errors.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
          )}
        </>
        )}
      </div>
    </div>
  )
}